import React from 'react';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import { Button } from '../Buttons/Button';
import { ExportButton, ExportButtonProps } from '../Buttons/ExportButton';
import { DetailsSubheader } from '../DetailsSubheader';
import { Icon, IconSize } from '../Icon';
import { SearchBox, SearchBoxProps } from '../SearchBox';
import { Typography } from '../Typography';
import Section from './components/Section';
export { Section as PageSection };
/** Shared shape for primary and optional secondary header actions */
export type LinkedListViewAddButtonConfig = {
  text: string;
  onClick: () => void;
  testId?: string;
  disabled?: boolean;
  variant?: 'contained' | 'outlined';
  /**
   * optional, defaults to false. When true, shows a spinner on the button and disables it - use while a
   * request triggered by this button is in flight
   */
  loading?: boolean;
};
export interface LinkedListViewProps {
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * main title for the page
   */
  pageTitle: string;
  /**
   * small viewport or not
   */
  isSmallDevice: boolean;
  /**
   * optional, override for the page container
   */
  dataTestId?: string;
  /**
   * optional, second header action rendered before the primary add button (e.g. outlined "new" next to contained "link")
   */
  secondaryAddButton?: LinkedListViewAddButtonConfig;
  /**
   * optional, object describing the add button
   */
  addButton?: LinkedListViewAddButtonConfig;
  /**
   * optional, object describing the export button
   */
  exportButton?: ExportButtonProps;
  /**
   * optional, if not provided the search box will not be displayed
   */
  searchBox?: SearchBoxProps;
  /**
   * optional, if not provided the filter button will not be displayed
   */
  filterButton?: {
    text: string;
    onClick: () => void;
  };
  /**
   * optional, component to render some quick filters underneath the search input
   */
  quickFiltersComponent?: React.ReactNode;
  /**
   * indicator and message for no results, will not render sections if noresults.isVisible = true
   */
  noResults?: {
    isVisible: boolean;
    message: string;
  };
  /**
   * rendered as long as noResults.isVisible is not true array of sections describing the main
   * content for the linked list
   */
  sections: {
    title?: string;
    testId?: string;
    key: string;
    children: JSX.Element | JSX.Element[];
  }[];
  /**
   * optional children for cases where the sections prop is not enough (like pagination or LinkForms)
   */
  children?: JSX.Element | JSX.Element[];
  /**
   * optional, when true removes the default x padding around content (per figma designs)
   */
  hideContentPx?: boolean;
  /**
   * optional, when true removes the default x padding on non-small devices
   */
  noPxOnLargeDevices?: boolean;
  /**
   * optional, subtitle for the page title
   */
  subtitle?: string;
  /**
   * optional, title count for the page title
   */
  titleCount?: number;
  /**
   * optional, top slot for the page title
   */
  topSlot?: React.ReactNode;
}
/**
 * Renders a LinkedListView which is a generic page template that helps drive consistency
 */
export const LinkedListView: React.FC<React.PropsWithChildren<LinkedListViewProps>> = ({
  pageTitle,
  isSmallDevice,
  dataTestId,
  secondaryAddButton,
  addButton,
  exportButton,
  searchBox,
  filterButton,
  quickFiltersComponent,
  noResults,
  sections,
  children,
  hideContentPx,
  noPxOnLargeDevices,
  subtitle = '',
  titleCount,
  topSlot,
}: LinkedListViewProps) => {
  const theme = useTheme<Theme>();
  const Search: JSX.Element | undefined = searchBox && (
    <>
      <Box display="flex" flexWrap="nowrap" sx={{ width: '100%', mb: 0, '& .MuiFormControl-root': { my: 0 } }}>
        <SearchBox
          {...searchBox}
          onSubmit={(searchValue: string) => {
            searchBox.onSubmit(searchValue);
          }}
          disableSelfSpacing={true}
          wrapperBoxProps={{ flex: 1 }}
        />
        {!isSmallDevice && filterButton ? (
          <Button
            data-testid="filter-button"
            sx={{ ml: 2 }}
            onClick={filterButton.onClick}
            color="secondary"
            variant="outlined"
            size="large"
            startIcon={<Icon size={IconSize.SM} folder="actions/23-filter" />}
          >
            {filterButton.text}
          </Button>
        ) : null}
      </Box>
      {quickFiltersComponent}
    </>
  );
  const Content = (
    <>
      {noResults && noResults?.isVisible ? (
        <Typography>{noResults.message}</Typography>
      ) : (
        sections?.map(({ key, ...rest }) => {
          return <Section key={key} {...rest} />;
        })
      )}
      {children}
    </>
  );
  const pagePx = isSmallDevice || noPxOnLargeDevices ? 0 : theme?.custom?.spacing?.sm;
  const contentPx = hideContentPx ? 0 : pagePx;
  const addButtonRowGap = theme.custom?.spacing?.md ?? 2;
  const Controls: JSX.Element = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: addButtonRowGap,
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        width: '100%',
      }}
    >
      {exportButton && (
        <ExportButton
          {...exportButton}
          isSmallDevice={isSmallDevice}
          dataTestId="linked-list-view-secondary-button"
          mr={0}
        />
      )}
      {(secondaryAddButton || addButton) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: addButtonRowGap,
            flexShrink: 0,
          }}
        >
          {secondaryAddButton && (
            <Button
              data-testid={secondaryAddButton.testId || 'linked-list-secondary-add-button'}
              color="primary"
              variant={secondaryAddButton.variant ?? 'outlined'}
              startIcon={
                <Icon
                  fillColor={
                    (secondaryAddButton.variant ?? 'outlined') === 'contained'
                      ? theme.palette.common.white
                      : theme.palette.primary.main
                  }
                  size={IconSize.SM}
                  folder="actions/02-add"
                />
              }
              onClick={secondaryAddButton.onClick}
              aria-label={secondaryAddButton.text}
              disabled={secondaryAddButton.disabled}
            >
              {secondaryAddButton.text}
            </Button>
          )}
          {addButton && (
            <Button
              data-testid={addButton?.testId || 'linked-list-add-button'}
              color="primary"
              variant={addButton.variant ?? 'contained'}
              startIcon={
                <Icon
                  fillColor={
                    (addButton.variant ?? 'contained') === 'outlined'
                      ? theme.palette.primary.main
                      : theme.palette.common.white
                  }
                  size={IconSize.SM}
                  folder="actions/02-add"
                />
              }
              onClick={addButton.onClick}
              aria-label={addButton?.text}
              disabled={addButton?.disabled}
              loading={addButton?.loading}
              loadingPosition="end"
            >
              {addButton.text}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
  const Title: JSX.Element = (
    <Box mb={6}>
      <DetailsSubheader title={pageTitle} subtitle={subtitle} titleCount={titleCount} controls={Controls} />
    </Box>
  );
  return (
    <Box data-testid={dataTestId ?? 'linked-list-view'}>
      <Box display="flex" flexDirection="column" px={pagePx}>
        {Title}
        {topSlot}
        {(Search || quickFiltersComponent) && (
          <Box
            data-testid="linked-list-view-search-row"
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
            mb={isSmallDevice ? 4 : 8}
            flexWrap="wrap"
          >
            {Search}
          </Box>
        )}
      </Box>
      <Box px={contentPx} pb={isSmallDevice ? 10 : undefined}>
        {Content}
      </Box>
    </Box>
  );
};
